import { Layout, Breadcrumb } from 'antd'
import React, { FC, memo, useMemo } from 'react'
import { useLocation, Link } from 'react-router-dom'
import styles from './index.module.scss'
import { useTranslation } from '@/services/i18n'
import { match } from 'path-to-regexp'

const { Content } = Layout

interface Props {
  children: React.ReactNode
  params?: Record<string, string>
}

type BreadcrumbTextFactory = (
  urlParams: Record<string, string>,
  compParams: Record<string, string>
) => string

const BreadcrumbLayout: FC<Props> = ({ children, params = {} }) => {
  const { t } = useTranslation()
  const breadcrumbNameMap: Record<string, BreadcrumbTextFactory> = {
    '/wallet': () => t('common.wallet'),
    '/wallet/:walletKey': (urlParams, compParams) => compParams[urlParams.walletKey],
    '/application': () => t('side.application'),
    '/application/autoSweep': () => 'Auto Sweep',
  }
  const location = useLocation()

  const pathSnippets = location.pathname.split('/').filter(i => i)

  const breadcrumbItems = useMemo(() => {
    return pathSnippets.map((_, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`

      const paths = Object.keys(breadcrumbNameMap)

      let text
      for (let path of paths) {
        const urlMatch = match(path)

        const result = urlMatch(url)

        if (result) {
          const urlParams = result.params as Record<string, string>

          text = breadcrumbNameMap[path](urlParams, params)

          break
        }
      }
      return (
        <Breadcrumb.Item key={url}>
          {index < pathSnippets.length - 1 ? <Link to={url}>{text}</Link> : text}
        </Breadcrumb.Item>
      )
    })
  }, [pathSnippets, params])

  return (
    <Layout className={styles.layout}>
      <Breadcrumb className={styles.breadcrumb}>{breadcrumbItems}</Breadcrumb>
      <Content>{children}</Content>
    </Layout>
  )
}

export default memo(BreadcrumbLayout)
